import React from "react";
import { H1, H4 } from "melodies-source/Text";
import styled from "styled-components";
import { InfoBars } from "Components/InfoBars";
import { Item as DonutChartItem, DonutChart } from "Components/DonutChart";
import { RefreshIcon } from "Components/RefreshIcon";
import { Card } from "Components/Card";
import { useArtistEventContext } from "contexts/ArtistEventContext";
import { NoData, TwoColumns } from "Components";
import { formatNumber } from "Utils/format";
import { useMultiDayEventContext } from "contexts/MultiDayEventContext";
import { useMobileMediaQuery } from "hooks";
import { CardTitle } from "../Components/CardTitle";
import { Loader } from "./Loader";

export const Clicks: React.FC = () => {
  const eventContext = useArtistEventContext();
  const {
    multidayEvent,
    multiDayReport,
    multiDayReportLoading,
    multiDayReportError,
  } = useMultiDayEventContext();
  const isMobile = useMobileMediaQuery();
  const clicks =
    multidayEvent && multiDayReport
      ? multiDayReport.clicks
      : eventContext.clicks;
  const clicksLoading = multidayEvent
    ? multiDayReportLoading
    : eventContext.clicksLoading;
  const clicksError = multiDayReport
    ? multiDayReportError
    : eventContext.clicksError;

  const categoriesTotal = clicks?.reduce(
    (total, item) => total + item.total,
    0,
  );
  const filteredClicks = clicks?.filter(({ total }) => total > 0);

  const data: DonutChartItem[] = filteredClicks.map(({ name, total }) => ({
    id: name,
    label: name,
    value: Math.round((total / categoriesTotal) * 100),
  }));

  const categories = filteredClicks.map(({ name, detail }) => (
    <Category key={name}>
      <CategoryName>Clicks: {name}</CategoryName>
      <InfoBars
        data={detail.map(({ name, count }) => ({
          label: name,
          value: count,
        }))}
      />
    </Category>
  ));

  return (
    <StyledCard isElevated>
      {clicksLoading ? (
        <Loader />
      ) : (
        <>
          <CardTitle>Click Details</CardTitle>
          {clicksError ? (
            <StyledRefreshIcon />
          ) : categoriesTotal > 0 ? (
            <>
              <ChartContainer>
                <TotalContainer>
                  <H4>Total Clicks</H4>
                  <H1>{formatNumber(categoriesTotal)}</H1>
                </TotalContainer>
                <DonutChart data={data} withVerticalLayout={isMobile} />
              </ChartContainer>
              <TwoColumns gap="30px">{categories}</TwoColumns>
            </>
          ) : (
            <NoData />
          )}
        </>
      )}
    </StyledCard>
  );
};

const StyledRefreshIcon = styled(RefreshIcon)`
  margin-top: 5px;
`;

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CategoryName = styled(H4)`
  color: var(--header-text-color);
`;

const Category = styled.div`
  text-align: left;
  width: 100%;
`;

const TotalContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  ${H4} {
    color: var(--secondary-text-color);
  }
`;

const ChartContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 65px;
  margin: 30px 0;

  ${({ theme }) => theme.media.mobile} {
    flex-direction: column;
    gap: 0;
    margin: 20px 0;
  }
`;
